import React, { useCallback, useEffect, useState } from "react";
import SimpleBar from "simplebar-react";
import { Button, Card, CardBody, CardHeader, Spinner } from "reactstrap";
import { Link } from "react-router-dom";
import Avatar from "components/common/Avatar";
import StatusBadge from "components/common/StatusBadge";
import { DateOnly } from "components/common/Date";
import { getWorkAssignments } from "features/WorkAssignments/services/WorkAssignmentService";
import {
  WorkAssignmentSourceType,
  workAssignmentStatusMap,
} from "features/WorkAssignments/constants/work-assignment.constants";
import type { WorkAssignment } from "features/WorkAssignments/types/work-assignment.types";
import { workAssignmentAssigneeId } from "features/WorkAssignments/utils/workAssignmentNormalize";
import WorkAssignmentForm from "features/WorkAssignments/WorkAssignmentForm";

export default function AssignmentsOverviewCard({
  sourceType,
  sourceId,
  sourceLabel,
  canAssign,
  canReassign,
  defaultPlannedStartAt,
  refreshKey,
  onSuccess,
}: {
  sourceType: WorkAssignmentSourceType;
  sourceId: number;
  sourceLabel?: string;
  canAssign?: boolean;
  canReassign?: boolean;
  defaultPlannedStartAt?: string | null;
  refreshKey?: number;
  onSuccess?: () => void;
}) {
  const [assignments, setAssignments] = useState<WorkAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [localRefresh, setLocalRefresh] = useState(0);
  const [assignFormOpen, setAssignFormOpen] = useState(false);
  const [assignFormIsReassign, setAssignFormIsReassign] = useState(false);

  const openAssign = useCallback(() => {
    setAssignFormIsReassign(false);
    setAssignFormOpen(true);
  }, []);

  const openReassign = useCallback(() => {
    setAssignFormIsReassign(true);
    setAssignFormOpen(true);
  }, []);

  const closeAssignmentForm = useCallback(() => {
    setAssignFormOpen(false);
    setAssignFormIsReassign(false);
  }, []);

  useEffect(() => {
    if (!sourceId) return;
    let cancelled = false;
    setLoading(true);
    getWorkAssignments({ sourceType, sourceId, limit: 20 })
      .then((res) => {
        if (cancelled) return;
        const rows: WorkAssignment[] = (res as any)?.data?.rows ?? [];
        setAssignments(
          rows.filter(
            (a) => a.sourceId === sourceId && a.sourceType === sourceType,
          ),
        );
      })
      .catch(() => {
        if (!cancelled) setAssignments([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sourceType, sourceId, refreshKey, localRefresh]);

  return (
    <>
      <Card>
        <CardHeader className="d-flex align-items-center justify-content-between">
          <h6 className="card-title mb-0">Assignments</h6>
          <div className="d-flex align-items-center gap-2">
            {canAssign ? (
              <Button
                type="button"
                size="sm"
                color="light"
                className="border py-0 px-2"
                onClick={openAssign}
                title="Assign user"
              >
                <i className="ri-user-follow-line align-middle me-1" />
                Assign
              </Button>
            ) : null}
            {(canReassign ?? canAssign) ? (
              <Button
                type="button"
                size="sm"
                color="light"
                className="border py-0 px-2"
                onClick={openReassign}
                title="Reassign"
              >
                <i className="ri-user-shared-line align-middle me-1" />
                Reassign
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {loading ? (
            <div className="d-flex justify-content-center py-4">
              <Spinner size="sm" />
            </div>
          ) : assignments.length === 0 ? (
            <p className="text-muted mb-0 small px-3 py-3">
              No assignments yet.
            </p>
          ) : (
            <SimpleBar style={{ maxHeight: 400 }}>
              <ul className="list-unstyled mb-0">
                {assignments.map((a, idx) => {
                  const u = a.user;
                  const assigneeId = workAssignmentAssigneeId(a);
                  const userId = u?.id ?? (assigneeId > 0 ? assigneeId : null);
                  const name = u
                    ? [u.firstName, u.lastName]
                        .filter(Boolean)
                        .join(" ")
                        .trim() ||
                      u.email ||
                      `User #${userId}`
                    : `User #${userId}`;
                  const avatarUrl = u?.avatar ?? undefined;
                  const employeeId = u?.employee?.id ?? null;
                  const href =
                    employeeId != null ? `/employees/${employeeId}` : null;
                  const isLast = idx === assignments.length - 1;

                  return (
                    <li
                      key={a.id}
                      className={`px-3 py-3${!isLast ? " border-bottom" : ""}`}
                    >
                      <div className="d-flex gap-3 align-items-start">
                        <div className="flex-shrink-0 mt-1">
                          <div
                            className="rounded-circle overflow-hidden"
                            style={{ width: 36, height: 36 }}
                          >
                            <Avatar
                              name={name}
                              image={avatarUrl}
                              className="fs-13 w-100 h-100"
                            />
                          </div>
                        </div>
                        <div className="flex-grow-1 min-w-0">
                          <div className="d-flex align-items-start justify-content-between gap-2">
                            <div className="min-w-0">
                              {href ? (
                                <Link
                                  to={href}
                                  className="fw-semibold text-body d-block text-truncate"
                                  style={{ fontSize: "0.875rem" }}
                                >
                                  {name}
                                </Link>
                              ) : (
                                <span
                                  className="fw-semibold text-body d-block text-truncate"
                                  style={{ fontSize: "0.875rem" }}
                                >
                                  {name}
                                </span>
                              )}
                              {a.role ? (
                                <span className="text-muted small">
                                  {a.role}
                                </span>
                              ) : null}
                            </div>
                            <div className="flex-shrink-0">
                              <StatusBadge
                                value={a.status}
                                map={workAssignmentStatusMap}
                              />
                            </div>
                          </div>

                          {a.plannedStartDate || a.plannedEndDate ? (
                            <div className="d-flex flex-wrap gap-3 mt-2">
                              {a.plannedStartDate ? (
                                <div className="d-flex align-items-center gap-1">
                                  <i
                                    className="ri-calendar-line text-muted"
                                    style={{ fontSize: "0.75rem" }}
                                  />
                                  <span className="text-muted small">
                                    Start:{" "}
                                    <span className="text-body">
                                      <DateOnly date={a.plannedStartDate} />
                                    </span>
                                  </span>
                                </div>
                              ) : null}
                              {a.plannedEndDate ? (
                                <div className="d-flex align-items-center gap-1">
                                  <i
                                    className="ri-calendar-check-line text-muted"
                                    style={{ fontSize: "0.75rem" }}
                                  />
                                  <span className="text-muted small">
                                    End:{" "}
                                    <span className="text-body">
                                      <DateOnly date={a.plannedEndDate} />
                                    </span>
                                  </span>
                                </div>
                              ) : null}
                            </div>
                          ) : null}

                          {a.remarks ? (
                            <div className="d-flex align-items-start gap-1 mt-2">
                              <i
                                className="ri-chat-1-line text-muted flex-shrink-0"
                                style={{ fontSize: "0.75rem", marginTop: 2 }}
                              />
                              <p
                                className="text-muted small mb-0"
                                style={{ whiteSpace: "pre-wrap" }}
                              >
                                {a.remarks}
                              </p>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </SimpleBar>
          )}
        </CardBody>
      </Card>

      <WorkAssignmentForm
        show={assignFormOpen}
        sourceType={sourceType}
        sourceId={sourceId}
        sourceLabel={sourceLabel ?? `#${sourceId}`}
        isReassign={assignFormIsReassign}
        defaultPlannedStartAt={defaultPlannedStartAt}
        onCloseClick={closeAssignmentForm}
        onSuccess={() => {
          closeAssignmentForm();
          setLocalRefresh((k) => k + 1);
          onSuccess?.();
        }}
      />
    </>
  );
}
